/* eslint-disable no-underscore-dangle */
import {
  Marker, Polyline, Point, DomUtil
} from 'leaflet'
import { interpolateOnLine } from 'leaflet-geometryutil'
import bearing from '@turf/bearing'

const FPS = 30

export default class MovingMarker extends Marker {
  initialize(latLngs, options) {
    super.initialize(latLngs[0], { // meters
      distance: 0,
      speed: 0,
      showPath: false,
      pathOptions: {},
      onUpdate: () => {
      },
      clickable: false,
      ...options
    })

    this.lastUpdate = Date.now()
    this.distance = this.options.distance
    this.speed = this.options.speed
    this.path = null
    this.setLine(latLngs)
  }

  setLine(latLngs) {
    this.latLngs = latLngs
    this.initPath()
    return this
  }

  setDistance(distance, speed = 0) {
    const now = Date.now()
    const interval = this.lastUpdate - now / 1000

    const diffDistance = distance - this.distance
    this.speed = diffDistance / interval + speed
    this.lastUpdate = now

    this.updatePosition()
    return this
  }

  setShowPath(value) {
    this.options.showPath = value
    if (value) {
      this.initPath()
    } else if (this.path) {
      this.path.remove()
      this.path = null
    }

    this.updatePosition()
    return this
  }

  initPath() {
    if (this.options.showPath && !this.path) {
      this.path = new Polyline([this.latLngs[0]], this.options.pathOptions)
    }

    if (this.path && this._map) {
      this.path.addTo(this._map)
    }
  }

  updatePosition(map = this._map) {
    if (!this.latLngs.length) {
      return this
    }

    if (map) {
      let totalDistance = 0
      let lastPoint
      this.latLngs.forEach((point) => {
        if (lastPoint) {
          totalDistance += lastPoint.distanceTo(point)
        }
        lastPoint = point
      })

      const { latLng, predecessor } = interpolateOnLine(map, this.latLngs, this.distance / totalDistance)

      super.setLatLng(latLng)

      const previousPoint = this.latLngs[predecessor]
      if (previousPoint) {
        this.setAngle(
          bearing([previousPoint.lng, previousPoint.lat], [latLng.lng, latLng.lat])
        )
      }

      // update line for covered distance
      if (this.path) {
        const path = this.latLngs.slice(0, predecessor + 1).concat(latLng)
        this.path.setLatLngs(path)
      }
    } else {
      super.setLatLng(this.latLngs[0])
    }

    return this
  }

  onAdd(map) {
    super.onAdd(map)
    if (this.path) {
      this.path.addTo(map)
    }
    this.updatePosition(map)
    setTimeout(() => {
      /*
        @todo: transitions allows markes to shorten the polyline
        therefore its better to add a 'speed' option and animate by changing the distance
      */
      // disabled because it looks weird when zooming
      if (this._icon) {
        this._icon.style.transition = 'transform .0333s linear'
      }
    }, 0)

    // start animation loop
    map.on('zoomstart', this.stop, this)
    map.on('movestart', this.stop, this)
    map.on('moveend', this.start, this)
    map.on('zoomend', this.start, this)
    this.start()
  }

  start() {
    this.stop()
    if (this.speed) {
      this.$interval = setInterval(this.animate.bind(this), 1000 / FPS)
    }
  }

  animate() {
    this.distance += this.speed / 3.6 / FPS
    this.updatePosition()
  }

  stop() {
    if (this.$interval) {
      clearInterval(this.$interval)
      this.$interval = null
    }
  }

  onRemove(map) {
    map.off('zoomstart', this.stop, this)
    map.off('movestart', this.stop, this)
    map.off('moveend', this.start, this)
    map.off('zoomend', this.start, this)
    this.stop()

    if (this.path) {
      this.path.remove()
    }
    super.onRemove(map)
  }

  setTitle(title) {
    if (this._icon) {
      this._icon.setAttribute('title', title)
    }
    return this
  }

  setAngle(angle) {
    if (this._icon) {
      this._icon.style.setProperty('--rotate', `${angle - 90}deg`) // cc-icon is rotated 90deg
    }
  }

  _setPos(pos) {
    if (this._icon) {
      this._icon._leaflet_pos = pos
      const point = pos || new Point(0, 0)
      this._icon.style.transform = `translate3d(${point.x}px, ${point.y}px, 0) rotateZ(var(--rotate, 0deg))`
      this._icon.style.transformOrigin = '50% 50%'
      // DomUtil.setPosition(this._icon, pos)
    }

    if (this._shadow) {
      DomUtil.setPosition(this._shadow, pos)
    }

    this._zIndex = pos.y + this.options.zIndexOffset

    this._resetZIndex()
  }
}
